import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { useDocumentParams } from 'app/router/params';
import { Spinner } from 'app/components';
import { NO_DRAFT, useApplyDraft, useDiscardDraft, useDraft, DesignDraft } from 'entities/draft';
import { useGetDocument } from 'entities/document';
import { useUser } from 'app/state/contexts';
import { Clickable } from 'shared/ui/Clickable';
import { useUiDebug } from 'app/utils/hooks/ui-debug';

export function DraftController() {
  const { t } = useTranslation('theme');

  const { documentId } = useDocumentParams();
  const { selectedOrganization } = useUser();
  const pDocument = useGetDocument(documentId, selectedOrganization);
  const draft = useDraft({ documentSuperId: documentId });
  const { mutate: discardDraft } = useDiscardDraft();
  const { mutate: applyDraft } = useApplyDraft();
  const showDebug = useUiDebug();

  const onApplyDraftClick = () => pDocument.data && applyDraft(pDocument.data);
  const onDiscardDraftClick = () => pDocument.data && discardDraft(pDocument.data);

  if (draft.isLoading || pDocument.isLoading || !pDocument.data) {
    return <Spinner />;
  }

  if (draft.isError || pDocument.isError) {
    return <div>Error fetching draft information</div>;
  }

  if (draft.data === NO_DRAFT) {
    return <></>;
  }

  return (
    <div className="position-fixed d-flex flex-column draft-info bg-white">
      <div className="d-flex flex-row py-2 px-3 gap-r-2 justify-content-between align-items-center">
        <Clickable className="text-blue-700 fs-5 text-uppercase d-flex flex-row gap-r-0-5 align-items-center" onClick={onDiscardDraftClick}>
          {t('discard-draft')}
          <i className="fa-regular fa-trash" />
        </Clickable>
        <div className="flex-grow-1">&nbsp;</div>
        <Link
          to="/documents/$documentId/pdf/$documentRevisionId"
          params={{ documentId: String(documentId), documentRevisionId: String(pDocument.data.revisionId) }}
          search={{ useDraft: true }}
          className="text-blue-700 fs-5 text-uppercase d-flex flex-row gap-r-1 align-items-center"
          target={'_blank'}
        >
          {t('preview-draft')}
          <i className="fa-regular fa-eye" />
        </Link>
        <Clickable className="btn btn-primary" onClick={onApplyDraftClick}>
          {t('apply-draft')}
        </Clickable>
      </div>
      {showDebug && (
        <ul style={{ listStyleType: 'disc' }} className="p-4">
          {((draft.data as DesignDraft).content as ReadonlyArray<{ type: string; templateSuperId?: string }> | null)?.map((d) => (
            <li key={d.templateSuperId || d.type}>{d.type}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
